import { useMemo, useState } from 'react';
import { Controller, useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Card, CardContent, Divider, Grid, TextField, Typography } from '@mui/material';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import { buildHotelGuestLabels, buildHotelGuestSlots, type HotelFlowState } from '../flowConfig';
import type { CurrentAccount } from '../../../api/types/reports';
import type { BillingInformation } from '../../../api/types/billing';
import { CurrentAccountAutocomplete } from '../../../components/CurrentAccountAutocomplete';
import { PhoneField } from '../../../components/PhoneField';

function formatBalance(limit?: number): string {
  if (limit === undefined || limit === null) return '—';
  return `${limit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
}

/** Air modülündeki `buildBillingInformation` ile aynı 3 dallı mantık: cari hesap
 *  seçiliyse onun vergi no'suyla kurumsal fatura; değilse elle girilen T.C./Vergi No ile
 *  bireysel fatura; ikisi de yoksa fallback (IfCompany=true, boş TaxNo). */
function buildBillingInformation(
  currentAccount: CurrentAccount | null,
  billingName: string,
  identityOrTaxNo: string | undefined
): BillingInformation {
  const base = {
    billingName,
    address: 'B',
    taxOffice: 'B',
    autoBill: false,
    serviceFee: 0,
  };
  const currentTaxNo = currentAccount?.taxNumber;
  if (currentTaxNo) {
    return { ...base, ifCompany: true, ifCurrentCodeSet: true, taxNo: currentTaxNo };
  }
  if (identityOrTaxNo) {
    return { ...base, ifCompany: false, ifCurrentCodeSet: false, taxNo: identityOrTaxNo };
  }
  return { ...base, ifCompany: true, ifCurrentCodeSet: false, taxNo: '' };
}

const guestSchema = z.object({
  firstName: z.string().min(1, 'Ad gerekli'),
  lastName: z.string().min(1, 'Soyad gerekli'),
  citizenNo: z
    .string()
    .min(1, 'TC Kimlik No gerekli')
    .regex(/^[1-9]\d{10}$/, 'Geçerli bir TC Kimlik No girin (11 haneli)'),
  birthDate: z.string().min(1, 'Doğum tarihi gerekli'),
});

const schema = z.object({
  contact: z.object({
    name: z.string().min(1, 'Ad gerekli'),
    lastName: z.string().min(1, 'Soyad gerekli'),
    phone: z.string().min(1, 'Telefon gerekli'),
    email: z.string().min(1, 'E-posta gerekli').email('Geçerli bir e-posta girin'),
  }),
  guests: z.array(guestSchema).min(1, 'En az bir misafir gerekli'),
  billing: z.object({
    billingName: z.string().min(1, 'Fatura adı gerekli'),
    identityOrTaxNo: z.string().optional(),
  }),
});

type FormValues = z.infer<typeof schema>;

export function GuestsStep() {
  const flow = useBookingFlowContext<HotelFlowState>();

  const guestSlots = useMemo(
    () => buildHotelGuestSlots(flow.state.searchRequest),
    [flow.state.searchRequest]
  );
  const guestCount = guestSlots.length;
  const guestLabels = useMemo(() => buildHotelGuestLabels(guestSlots), [guestSlots]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      contact: { name: '', lastName: '', phone: '', email: '' },
      guests: Array.from({ length: guestCount }, () => ({ firstName: '', lastName: '', citizenNo: '', birthDate: '' })),
      billing: { billingName: '', identityOrTaxNo: '' },
    },
  });

  const [currentAccount, setCurrentAccount] = useState<CurrentAccount | null>(null);
  const handleCurrentAccountChange = (value: CurrentAccount | null) => {
    setCurrentAccount(value);
    if (value) {
      setValue('billing.billingName', value.title1 || value.title2 || '');
    }
  };

  const onSubmit = async (values: FormValues) => {
    const billingInformation = buildBillingInformation(
      currentAccount,
      values.billing.billingName,
      values.billing.identityOrTaxNo
    );
    await flow.runStep({
      contact: values.contact,
      guests: values.guests,
      selectedCurrentAccount: currentAccount ?? undefined,
      billingInformation,
    });
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          İletişim Bilgileri
        </Typography>
        {flow.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {flow.error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextFieldRow
                label="Ad"
                error={errors.contact?.name?.message}
                register={register('contact.name')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextFieldRow
                label="Soyad"
                error={errors.contact?.lastName?.message}
                register={register('contact.lastName')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="contact.phone"
                render={({ field }) => (
                  <PhoneField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label="Telefon"
                    error={!!errors.contact?.phone}
                    helperText={errors.contact?.phone?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextFieldRow
                label="E-posta"
                error={errors.contact?.email?.message}
                register={register('contact.email')}
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle1">Misafir Bilgileri</Typography>
          {Array.from({ length: guestCount }).map((_, index) => (
            <Box key={index}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {guestLabels[index] ?? `${index + 1}. Misafir`}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextFieldRow
                    label="Ad"
                    error={errors.guests?.[index]?.firstName?.message}
                    register={register(`guests.${index}.firstName` as const)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextFieldRow
                    label="Soyad"
                    error={errors.guests?.[index]?.lastName?.message}
                    register={register(`guests.${index}.lastName` as const)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextFieldRow
                    label="TC Kimlik No"
                    error={errors.guests?.[index]?.citizenNo?.message}
                    register={register(`guests.${index}.citizenNo` as const)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Doğum Tarihi"
                    size="small"
                    fullWidth
                    type="date"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.guests?.[index]?.birthDate}
                    helperText={errors.guests?.[index]?.birthDate?.message}
                    {...register(`guests.${index}.birthDate` as const)}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          <Divider />
          <Typography variant="subtitle1">Cari Hesap / Fatura Bilgileri</Typography>
          <Typography variant="body2" color="text.secondary">
            Kurumsal cari hesap üzerinden ödeme yapılacaksa hesabı seçin (opsiyonel) — seçilirse fatura adı/vergi
            no arka planda hesaptan doldurulur. Cari hesap seçilmezse bireysel fatura için T.C. Kimlik/Vergi No
            girebilirsiniz.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CurrentAccountAutocomplete value={currentAccount} onChange={handleCurrentAccountChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              {currentAccount ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Bakiye"
                  value={formatBalance(currentAccount.limit)}
                  slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                />
              ) : (
                <TextFieldRow
                  label="Fatura Adı"
                  error={errors.billing?.billingName?.message}
                  register={register('billing.billingName')}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="T.C. Kimlik / Vergi No"
                disabled={!!currentAccount}
                helperText={currentAccount ? "Cari hesabın vergi no'su kullanılıyor" : undefined}
                {...register('billing.identityOrTaxNo')}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={isSubmitting || flow.status === 'running'}>
              {flow.status === 'running' ? 'Kaydediliyor…' : 'Devam Et'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/** Small local helper to cut down on repeated TextField error/helperText boilerplate. */
function TextFieldRow({
  label,
  error,
  register,
}: {
  label: string;
  error?: string;
  register: UseFormRegisterReturn;
}) {
  return (
    <TextField
      label={label}
      size="small"
      fullWidth
      error={!!error}
      helperText={error}
      {...register}
    />
  );
}
